import type { PriceHistoryPointDto } from '@/lib/models';

/** One plotted point: probability 0..1 at unix seconds. */
export interface SeriesPoint {
  t: number;
  p: number;
}

export interface BuildSeriesOptions {
  /**
   * Current Yes price (0–1) to anchor the line at `endTs`. Plaee only emits
   * buckets for periods that traded, so without an anchor each line dies at
   * its last trade and disagrees with the price shown on the option rows.
   */
  currentPrice?: number | null;
  /** Right edge of the chart window (unix seconds, the container's frozen "now"). */
  endTs?: number | null;
}

/**
 * Normalises one outcome's raw Plaee price history into plottable points.
 *
 * - `closePrice` (0–1) keyed by period-end seconds, nulls dropped, time-sorted
 *   (last value wins on duplicate timestamps).
 * - Interior gaps (periods with no trades) are step-filled flat at the previous
 *   close — the price genuinely didn't move, so rendering a curve-interpolated
 *   drift across the gap would misrepresent it.
 * - When `currentPrice` and `endTs` are given, the stale tail is flat-filled and
 *   the line is anchored at `endTs` with the current price, so every open line
 *   reaches the right edge and the resting label matches the buy bar. A closed
 *   (settled) chart passes no anchor and stays frozen at its last trade.
 */
export function buildSeriesPoints(history: PriceHistoryPointDto[], options: BuildSeriesOptions): SeriesPoint[] {
  const traded = history
    .filter((b) => b.closePrice != null)
    .map((b) => ({ t: Math.floor(Number(b.endPeriodTs)), p: b.closePrice as number, interval: b.periodInterval }))
    .sort((a, b) => a.t - b.t);
  if (traded.length === 0) return [];

  const intervalSec = (traded[0].interval || 60) * 60;
  const points: SeriesPoint[] = [];
  for (const { t, p } of traded) {
    const prev = points[points.length - 1];
    if (prev && prev.t === t) {
      prev.p = p;
      continue;
    }
    if (prev) fillFlat(points, prev, t, intervalSec);
    points.push({ t, p });
  }

  const { currentPrice, endTs } = options;
  if (currentPrice != null && endTs != null) {
    // The anchor owns "now": Plaee clamps its final (partial) bucket to the
    // request end, so a close can land exactly at endTs — drop it, or the
    // label would show the bucket close instead of the option row's price.
    while (points.length > 0 && points[points.length - 1].t >= endTs) points.pop();
    if (points.length > 0) fillFlat(points, points[points.length - 1], endTs, intervalSec);
    if (points.length > 0) points.push({ t: endTs, p: currentPrice });
  }
  return points;
}

/** Repeats `from.p` on the bucket grid up to (exclusive) `untilTs`. */
function fillFlat(points: SeriesPoint[], from: SeriesPoint, untilTs: number, intervalSec: number): void {
  for (let t = from.t + intervalSec; t < untilTs; t += intervalSec) {
    points.push({ t, p: from.p });
  }
}
